import { Injectable, signal } from '@angular/core';
import { invalidateUserDateFormatCache } from '@shared/utils/kl-date-format';

/** Row shape from `GET user-role-permission/role/{roleId}`. */
export interface RolePermissionRow {
  auth_num?: string;
  authorized?: string;
}

/**
 * Shape aligned with GET/POST `user-env` (`BaseResponse.data`) and `sessionStorage.userEnv`.
 * Server may include audit fields (`created_date`, …); clients typically use `company`, `working_period`, `language`.
 */
export interface UserEnvStored {
  id?: string;
  key?: string;
  company?: {
    id?: number | string;
    description?: string;
    has_material?: boolean;
    has_current_account?: boolean;
    has_service_card?: boolean;
    has_cash_account?: boolean;
    has_bank_card?: boolean;
    has_customer?: boolean;
    eintegrator_type?: string;
    [key: string]: unknown;
  };
  working_period?: {
    id?: number | string;
    status?: string;
    description?: string;
    number?: string | number;
    data_source?: string;
    active_period?: string;
    begin_date?: number;
    end_date?: number;
    company?: { id?: number | string; description?: string; [key: string]: unknown };
    [key: string]: unknown;
  };
  language?: string;
  date_format?: string;
  current_culture?: string;
  /** Fine-grained UI auth grants for the current user's role. When absent, action visibility fails closed. */
  role_permission?: RolePermissionRow[];
  /** App user id from GET `user/my` (`id`); used for custom report menu `USER` level matching. */
  user_id?: number | string;
  /** The current user's app-level role type (`user/my`'s `roles[0].role.type`, e.g. `ROLE_ADMIN`). */
  primary_role_type?: string;
  /** The current user's full name (`user/my`'s `full_name`). */
  full_name?: string;
  /** The current user's primary role display name (`user/my`'s `roles[0].role.description`). */
  role_description?: string;
  /** The current user's assigned branch, resolved from `user/my`'s `branch_id` via a follow-up `branch/{id}` lookup. */
  branch?: {
    id?: number | string;
    number?: string | number;
    description?: string;
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

@Injectable({ providedIn: 'root' })
export class UserEnvService {
  private readonly _env = signal<UserEnvStored | null>(null);

  /** Current user environment (mirrors `sessionStorage.userEnv` when set). */
  readonly env = this._env.asReadonly();

  constructor() {
    this.loadFromSession();
  }

  /** Hydrate from `sessionStorage` (e.g. after navigation from auth). */
  loadFromSession(): void {
    try {
      const raw = sessionStorage.getItem('userEnv');
      if (raw) {
        this._env.set(JSON.parse(raw) as UserEnvStored);
      } else {
        this._env.set(null);
      }
    } catch {
      this._env.set(null);
    }
  }

  /** Persist server payload and broadcast to subscribers (header, config bar, etc.). */
  applyFromRemote(data: UserEnvStored | null | undefined): void {
    if (data == null) {
      this.clear();
      return;
    }
    sessionStorage.setItem('userEnv', JSON.stringify(data));
    this._env.set(data);
    invalidateUserDateFormatCache();
  }

  clear(): void {
    sessionStorage.removeItem('userEnv');
    this._env.set(null);
    invalidateUserDateFormatCache();
  }
}
